import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import axios from 'axios';
import './VoiceAssistantOnboarding.css';
import BrandingSetup from '../Dashboard/BrandingSetup';

// Azure Speech Configuration
const API_KEY = process.env.REACT_APP_COG_SERVICE_KEY;
const API_LOCATION = process.env.REACT_APP_COG_SERVICE_LOCATION;
const speechConfig = API_KEY && API_LOCATION ? SpeechSDK.SpeechConfig.fromSubscription(API_KEY, API_LOCATION) : null;
if (speechConfig) {
  speechConfig.speechRecognitionLanguage = "en-US";
}

// Constants
const MIN_RESPONSE_LENGTH = 2; // Minimum characters for a valid response
const API_TIMEOUT = 10000; // 10 seconds timeout for API calls
const SPEAK_DELAY = 300; // Delay before speaking to ensure component is ready
const LISTEN_RESUME_DELAY = 800; // Delay before resuming listening after speaking

const VoiceAssistantOnboarding = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const organizerId = location.state?.organizerId;

  // State
  const [conversation, setConversation] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [recognisingText, setRecognisingText] = useState('');
  const [onboardingData, setOnboardingData] = useState({
    intent: '',
    startup_stage: '',
    evaluation_focus: '',
    assessment_expectations: '',
  });
  const [currentStep, setCurrentStep] = useState(0);
  
  // Brand Setup Popup State
  const [showBrandSetup, setShowBrandSetup] = useState(false);

  // Refs for values that need to be accessed in callbacks
  const recognizerRef = useRef(null);
  const audioStreamRef = useRef(null);
  const recognitionActiveRef = useRef(false);
  const conversationRef = useRef([]);
  const currentAudioRef = useRef(null);
  const audioUrlRefs = useRef([]); // Track all audio URLs for cleanup
  const isSpeakingRef = useRef(false);
  const lastProcessedResponseRef = useRef(null);
  const processingResponseRef = useRef(false);
  const isMountedRef = useRef(true);
  const currentStepRef = useRef(0);
  const speakTimeoutRef = useRef(null);
  const listenTimeoutRef = useRef(null);
  const apiAbortControllerRef = useRef(null);
  const onSpeechCompleteRef = useRef(null); // Callback when speech finishes

  const questions = [
    {
      key: 'intent',
      question:
        "Hello! How are you today? 😊 Welcome to SearchJob. To get started, can you tell me what you’d like to do here — for example, assess startups, hire early-stage teams, or screen founders?",
      followUp:
        "Got it! That helps a lot."
    },
    {
      key: 'startup_stage',
      question:
        "What stage of startups are you most interested in evaluating — idea stage, early traction, or growth-stage teams?",
      followUp:
        "Perfect, that gives me good context."
    },
    {
      key: 'evaluation_focus',
      question:
        "When assessing a startup, what matters most to you — the founding team, product and technology, market potential, traction, or something else?",
      followUp:
        "That’s a great focus."
    },
    {
      key: 'assessment_expectations',
      question:
        "What would you ideally like to learn from an assessment? For example, founder thinking, technical depth, execution ability, or business clarity.",
      followUp:
        "Excellent! I’ve captured your preferences."
    }
  ];

  // Sync currentStep ref with state
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  // Initialize component
  useEffect(() => {
    isMountedRef.current = true;

    if (!organizerId) {
      navigate('/organizer/register');
      return;
    }

    // Start with first question after a short delay
    if (currentStep === 0 && conversation.length === 0) {
      const timeout = setTimeout(() => {
        if (isMountedRef.current && addMessageRef.current) {
          addMessageRef.current('assistant', questions[0].question);
          setCurrentQuestion(questions[0].question);
        }
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, []);

  // Initialize Azure STT
  useEffect(() => {
    if (speechConfig && organizerId && isMountedRef.current) {
      initializeAzureSTT();
    }

    return () => {
      isMountedRef.current = false;
      cleanupAll();
    };
  }, [organizerId]);

  // Cleanup recognizer (defined first to avoid circular dependency)
  const cleanupRecognizer = useCallback(async () => {
    if (recognizerRef.current) {
      recognitionActiveRef.current = false;
      try {
        if (recognizerRef.current.stopContinuousRecognitionAsync) {
          await new Promise((resolve) => {
            recognizerRef.current.stopContinuousRecognitionAsync(
              () => resolve(),
              () => resolve()
            );
          });
        }
      } catch (_) {}
      try {
        recognizerRef.current.close();
      } catch (_) {}
      recognizerRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
  }, []);

  // Cleanup all resources
  const cleanupAll = useCallback(() => {
    // Clear all timeouts and callbacks
    if (speakTimeoutRef.current) clearTimeout(speakTimeoutRef.current);
    if (listenTimeoutRef.current) clearTimeout(listenTimeoutRef.current);
    onSpeechCompleteRef.current = null;

    // Abort any pending API calls
    if (apiAbortControllerRef.current) {
      apiAbortControllerRef.current.abort();
      apiAbortControllerRef.current = null;
    }

    // Cleanup recognizer
    cleanupRecognizer();

    // Cleanup audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    // Revoke all audio URLs to prevent memory leaks
    audioUrlRefs.current.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // Ignore errors
      }
    });
    audioUrlRefs.current = [];

    // Cancel speech synthesis
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, [cleanupRecognizer]);

  // Define speak function first (used by addMessage)
  const speakRef = useRef(null);
  
  const addMessage = useCallback((role, text) => {
    if (!isMountedRef.current || !text) return;

    const newMessage = {
      role,
      text,
      id: `${role}-${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
    };
    conversationRef.current = [...conversationRef.current, newMessage];
    setConversation([...conversationRef.current]);
    
    // Speak assistant messages
    if (role === 'assistant' && text && speakRef.current) {
      if (speakTimeoutRef.current) clearTimeout(speakTimeoutRef.current);
      speakTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current && speakRef.current) {
          speakRef.current(text);
        }
      }, SPEAK_DELAY);
    }
  }, []);

  // Store function refs to avoid circular dependencies
  const handleUserResponseRef = useRef(null);
  const startListeningRef = useRef(null);

  const initializeAzureSTT = useCallback(async () => {
    if (!speechConfig || !isMountedRef.current) {
      console.warn("Azure Speech config not available");
      return;
    }

    try {
      if (recognizerRef.current) {
        await cleanupRecognizer();
      }

      const constraints = {
        video: false,
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          sampleSize: 16,
          volume: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      };

      const audioStream = await navigator.mediaDevices.getUserMedia(constraints);
      if (!isMountedRef.current) {
        audioStream.getTracks().forEach(t => t.stop());
        return;
      }

      audioStreamRef.current = audioStream;
      const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(audioStream);
      recognizerRef.current = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

      recognizerRef.current.recognizing = (s, e) => {
        if (!isMountedRef.current) return;
        const interimText = e.result?.text || '';
        setRecognisingText(interimText);
      };

      recognizerRef.current.recognized = (s, e) => {
        if (!isMountedRef.current) return;

        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          const newText = e.result.text?.trim();
          setRecognisingText('');

          // Validate response
          if (!newText || newText.length < MIN_RESPONSE_LENGTH) {
            return;
          }

          // Check if we should process this response
          const shouldProcess = 
            !isProcessing && 
            !isSpeakingRef.current && 
            !processingResponseRef.current &&
            recognitionActiveRef.current;

          if (shouldProcess && handleUserResponseRef.current) {
            // Prevent duplicate processing
            const responseKey = `${currentStepRef.current}-${newText}`;
            if (lastProcessedResponseRef.current !== responseKey) {
              lastProcessedResponseRef.current = responseKey;
              handleUserResponseRef.current(newText);
            }
          }
        }
      };

      recognizerRef.current.canceled = (s, e) => {
        if (!isMountedRef.current) return;
        console.warn("Recognition canceled:", e);
        recognitionActiveRef.current = false;
        setIsListening(false);
      };

      recognizerRef.current.sessionStopped = (s, e) => {
        if (!isMountedRef.current) return;
        recognitionActiveRef.current = false;
        setIsListening(false);
      };

      if (isMountedRef.current && startListeningRef.current) {
        startListeningRef.current();
      }
    } catch (err) {
      console.error("Error initializing Azure STT:", err);
      if (isMountedRef.current) {
        setIsListening(false);
      }
    }
  }, [cleanupRecognizer]);

  const startListening = useCallback(async () => {
    if (!recognizerRef.current || recognitionActiveRef.current || !isMountedRef.current) return;

    recognitionActiveRef.current = true;
    setIsListening(true);

    try {
      recognizerRef.current.startContinuousRecognitionAsync(
        () => {
          if (isMountedRef.current) {
            console.log('[Azure] Recognition started');
          }
        },
        (err) => {
          if (isMountedRef.current) {
            console.error('[Azure] Start error:', err);
            recognitionActiveRef.current = false;
            setIsListening(false);
          }
        }
      );
    } catch (err) {
      console.error("startListening error", err);
      if (isMountedRef.current) {
        recognitionActiveRef.current = false;
        setIsListening(false);
      }
    }
  }, []);

  // Store ref after definition
  startListeningRef.current = startListening;

  const stopListening = useCallback(async () => {
    if (!recognizerRef.current || !recognitionActiveRef.current) return;

    recognitionActiveRef.current = false;
    return new Promise((resolve) => {
      recognizerRef.current.stopContinuousRecognitionAsync(
        () => {
          if (isMountedRef.current) {
            setIsListening(false);
            setRecognisingText('');
          }
          resolve();
        },
        (err) => {
          console.warn("Azure stop error", err);
          if (isMountedRef.current) {
            setIsListening(false);
          }
          resolve();
        }
      );
    });
  }, []);

  const generateAudio = useCallback(async (sentence) => {
    if (!sentence || !isMountedRef.current) return null;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      const response = await axios.post(
        "/api/generate_audio",
        { text: sentence },
        { 
          responseType: "blob",
          signal: controller.signal,
          timeout: API_TIMEOUT
        }
      );

      clearTimeout(timeoutId);

      if (!isMountedRef.current) return null;

      const audioUrl = URL.createObjectURL(response.data);
      audioUrlRefs.current.push(audioUrl);
      return audioUrl;
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error("Error generating audio:", error);
      }
      return null;
    }
  }, []);

  const speak_def = useCallback(async (textToSpeak) => {
    if (!textToSpeak || !isMountedRef.current) return;

    return new Promise((resolve) => {
      if ('speechSynthesis' in window && isMountedRef.current) {
        // Cancel any ongoing speech
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(textToSpeak);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 1;
        
        utterance.onend = () => {
          if (isMountedRef.current) {
            resolve();
          }
        };
        
        utterance.onerror = () => {
          resolve();
        };
        
        window.speechSynthesis.speak(utterance);
      } else {
        resolve();
      }
    });
  }, []);

  const playAudio = useCallback((url) => {
    return new Promise((resolve, reject) => {
      if (!url || !isMountedRef.current) {
        resolve();
        return;
      }

      const audio = new Audio(url);
      currentAudioRef.current = audio;

      audio.onended = () => {
        if (isMountedRef.current) {
          currentAudioRef.current = null;
          resolve();
        }
      };

      audio.onerror = (e) => {
        console.error("Audio playback error:", e);
        currentAudioRef.current = null;
        reject(e);
      };

      audio.play()
        .then(() => {
          // Audio started successfully
        })
        .catch((err) => {
          console.error("Error playing audio:", err);
          currentAudioRef.current = null;
          reject(err);
        });
    });
  }, []);

  // Store speak function in ref
  const speak = useCallback(async (text) => {
    if (!text || text.trim() === '' || !isMountedRef.current) return;

    // Stop any ongoing speech
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    // Cancel speech synthesis
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    // Stop listening while speaking
    if (recognitionActiveRef.current) {
      await stopListening();
    }

    if (!isMountedRef.current) return;

    isSpeakingRef.current = true;
    setIsSpeaking(true);

    // Split text into sentences
    const sentences = text
      .split(/[.!?]+\s+/)
      .map(sentence => sentence.trim())
      .filter(Boolean)
      .map(sentence => {
        // Ensure sentence ends with punctuation
        if (!/[.!?]$/.test(sentence)) {
          return sentence + '.';
        }
        return sentence;
      });

    if (sentences.length === 0) {
      isSpeakingRef.current = false;
      setIsSpeaking(false);
      if (isMountedRef.current && recognizerRef.current && !recognitionActiveRef.current && !isProcessing) {
        if (listenTimeoutRef.current) clearTimeout(listenTimeoutRef.current);
        listenTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current && !isProcessing) {
            startListening();
          }
        }, LISTEN_RESUME_DELAY);
      }
      return;
    }

    try {
      const fetchAudio = async (sentence) => {
        if (!isMountedRef.current) return null;
        try {
          return await generateAudio(sentence);
        } catch (error) {
          return null;
        }
      };

      // Fetch and play the first sentence
      let currentAudioUrl = await fetchAudio(sentences[0]);

      // Play remaining sentences sequentially
      for (let i = 1; i < sentences.length; i++) {
        if (!isMountedRef.current) break;

        const nextAudioPromise = fetchAudio(sentences[i]);
        
        if (currentAudioUrl === null) {
          await speak_def(sentences[i - 1]);
        } else {
          try {
            await playAudio(currentAudioUrl);
            // Cleanup URL after playing
            try {
              URL.revokeObjectURL(currentAudioUrl);
              audioUrlRefs.current = audioUrlRefs.current.filter(url => url !== currentAudioUrl);
            } catch (e) {}
          } catch (error) {
            await speak_def(sentences[i - 1]);
          }
        }
        
        currentAudioUrl = await nextAudioPromise;
      }

      // Play the final sentence
      if (isMountedRef.current) {
        if (currentAudioUrl === null) {
          await speak_def(sentences[sentences.length - 1]);
        } else {
          try {
            await playAudio(currentAudioUrl);
            // Cleanup URL after playing
            try {
              URL.revokeObjectURL(currentAudioUrl);
              audioUrlRefs.current = audioUrlRefs.current.filter(url => url !== currentAudioUrl);
            } catch (e) {}
          } catch (error) {
            await speak_def(sentences[sentences.length - 1]);
          }
        }
      }
    } catch (error) {
      console.error("Error in speak function:", error);
      if (isMountedRef.current) {
        await speak_def(text);
      }
    } finally {
      if (isMountedRef.current) {
        isSpeakingRef.current = false;
        setIsSpeaking(false);
        
        // Call the speech complete callback if set
        if (onSpeechCompleteRef.current) {
          const callback = onSpeechCompleteRef.current;
          onSpeechCompleteRef.current = null;
          callback();
        }
        
        // Resume listening after speaking
        if (recognizerRef.current && !recognitionActiveRef.current && !isProcessing) {
          if (listenTimeoutRef.current) clearTimeout(listenTimeoutRef.current);
          listenTimeoutRef.current = setTimeout(async () => {
            if (isMountedRef.current && recognizerRef.current && !recognitionActiveRef.current && !isProcessing) {
              await startListening();
            }
          }, LISTEN_RESUME_DELAY);
        }
      }
    }
  }, [stopListening, generateAudio, speak_def, playAudio, startListening]);

  // Store speak function in ref after definition
  speakRef.current = speak;

  // Store addMessage and saveOnboardingData refs
  const addMessageRef = useRef(null);
  const saveOnboardingDataRef = useRef(null);

  const handleUserResponse = useCallback(async (response) => {
    if (isProcessing || processingResponseRef.current || !isMountedRef.current) return;

    processingResponseRef.current = true;
    setIsProcessing(true);
    if (addMessageRef.current) {
      addMessageRef.current('user', response);
    }

    // Store the response
    const step = currentStepRef.current;
    const currentQ = questions[step];
    if (currentQ) {
      setOnboardingData((prev) => ({
        ...prev,
        [currentQ.key]: response,
      }));
    }

    // Create abort controller for API call
    apiAbortControllerRef.current = new AbortController();

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      const aiResponse = await fetch('/api/organizer/onboarding-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({
          organizer_id: organizerId,
          question: currentQ?.question || '',
          user_response: response,
          step: step,
          persona: location.state?.persona,
        }),
      });

      clearTimeout(timeoutId);

      if (!isMountedRef.current) return;

      if (!aiResponse.ok) {
        throw new Error(`HTTP error! status: ${aiResponse.status}`);
      }

      const contentType = aiResponse.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Response is not JSON");
      }

      const data = await aiResponse.json();
      const followUpText = data.follow_up || currentQ?.followUp || "Thank you! Let's continue.";

      if (isMountedRef.current && addMessageRef.current) {
        // Move to next step
        if (step < questions.length - 1) {
          const nextQ = questions[step + 1];
          
          // Check if follow-up already ends with a question (asking something)
          const followUpEndsWithQuestion = followUpText.trim().endsWith('?');
          
          if (followUpEndsWithQuestion) {
            // Follow-up already contains a question, just update step - don't add another question
            onSpeechCompleteRef.current = () => {
              if (!isMountedRef.current) return;
              const nextStep = step + 1;
              setCurrentStep(nextStep);
              lastProcessedResponseRef.current = null;
              if (nextQ) {
                setCurrentQuestion(nextQ.question);
              }
            };
            
            // Add only the follow-up message (which already has a question)
            addMessageRef.current('assistant', followUpText);
          } else {
            // Follow-up doesn't have a question, so add next question after it
            onSpeechCompleteRef.current = () => {
              if (!isMountedRef.current) return;
              const nextStep = step + 1;
              setCurrentStep(nextStep);
              lastProcessedResponseRef.current = null;
              if (nextQ && addMessageRef.current) {
                addMessageRef.current('assistant', nextQ.question);
                setCurrentQuestion(nextQ.question);
              }
            };
            
            // Add the follow-up message (this triggers speech)
            addMessageRef.current('assistant', followUpText);
          }
          
        } else {
          // Last question answered - first acknowledge, then save after speech completes
          onSpeechCompleteRef.current = () => {
            if (isMountedRef.current && saveOnboardingDataRef.current) {
              saveOnboardingDataRef.current();
            }
          };
          
          // Say acknowledgment first (this triggers speech, then callback saves)
          addMessageRef.current('assistant', followUpText);
        }
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        // Request was aborted, component may be unmounting
        return;
      }

      console.error('Error processing response:', err);
      
      if (isMountedRef.current) {
        const step = currentStepRef.current;
        const followUpText = currentQ?.followUp || "Thank you! Let's continue.";
        
        if (step < questions.length - 1) {
          const nextQ = questions[step + 1];
          const followUpEndsWithQuestion = followUpText.trim().endsWith('?');
          
          if (followUpEndsWithQuestion) {
            // Follow-up already has a question
            onSpeechCompleteRef.current = () => {
              if (!isMountedRef.current) return;
              const nextStep = step + 1;
              setCurrentStep(nextStep);
              lastProcessedResponseRef.current = null;
              if (nextQ) {
                setCurrentQuestion(nextQ.question);
              }
            };
            
            if (addMessageRef.current) {
              addMessageRef.current('assistant', followUpText);
            }
          } else {
            // Need to add next question separately
            onSpeechCompleteRef.current = () => {
              if (!isMountedRef.current) return;
              const nextStep = step + 1;
              setCurrentStep(nextStep);
              lastProcessedResponseRef.current = null;
              if (nextQ && addMessageRef.current) {
                addMessageRef.current('assistant', nextQ.question);
                setCurrentQuestion(nextQ.question);
              }
            };
            
            if (addMessageRef.current) {
              addMessageRef.current('assistant', followUpText);
            }
          }
          
        } else {
          // Last question - acknowledge first, then save
          onSpeechCompleteRef.current = () => {
            if (isMountedRef.current && saveOnboardingDataRef.current) {
              saveOnboardingDataRef.current();
            }
          };
          
          if (addMessageRef.current) {
            addMessageRef.current('assistant', followUpText);
          }
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
        processingResponseRef.current = false;
      }
      apiAbortControllerRef.current = null;
    }
  }, []);

  // Store handleUserResponse in ref after definition
  handleUserResponseRef.current = handleUserResponse;

  const saveOnboardingData = useCallback(async () => {
    if (!isMountedRef.current) return;

    // Validate organizerId
    if (!organizerId || organizerId === 'undefined' || organizerId === 'null') {
      console.error('Cannot save onboarding data: organizerId is missing or invalid', organizerId);
      if (addMessageRef.current) {
        addMessageRef.current('assistant', "There was an error. Please try registering again.");
      }
      return;
    }

    // Validate onboarding data
    if (
      !onboardingData.intent &&
      !onboardingData.startup_stage &&
      !onboardingData.evaluation_focus &&
      !onboardingData.assessment_expectations
    ) {
      console.warn('Onboarding data is empty');
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      // Ensure organizerId is a string and properly encoded
      const encodedOrganizerId = encodeURIComponent(String(organizerId));
      const url = `/api/organizer/${encodedOrganizerId}/onboarding`;
      
      console.log('Saving onboarding data to:', url);
      console.log('Onboarding data:', onboardingData);
      console.log('Organizer ID:', organizerId);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify(onboardingData),
      });

      clearTimeout(timeoutId);

      if (!isMountedRef.current) return;

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Get error message from response if available
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData = null;
        
        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } else {
            const text = await response.text();
            console.error('Non-JSON error response:', text);
            errorMessage = text || response.statusText || errorMessage;
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
          errorMessage = response.statusText || errorMessage;
        }
        
        console.error('Save onboarding error:', {
          status: response.status,
          message: errorMessage,
          errorData: errorData,
          url: url,
          organizerId: organizerId
        });
        
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Response is not JSON");
      }

      const data = await response.json();

      if (isMountedRef.current) {
        if (response.ok && addMessageRef.current) {
          // Set callback to show brand setup AFTER speech completes
          onSpeechCompleteRef.current = () => {
            if (isMountedRef.current) {
              // Stop listening for brand setup (uses form input)
              stopListening();
              setShowBrandSetup(true);
            }
          };
          addMessageRef.current('assistant', "Perfect! Your preferences are saved. Now let's set up your brand identity. You can also skip this step if you prefer.");
        } else if (addMessageRef.current) {
          addMessageRef.current('assistant', "There was an error saving your information. Please try again.");
        }
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return;
      }
      console.error('Error saving onboarding data:', err);
      if (isMountedRef.current && addMessageRef.current) {
        addMessageRef.current('assistant', "There was an error. Let's try again.");
      }
    }
  }, [organizerId, onboardingData, navigate]);

  // Store refs after all functions are defined
  useEffect(() => {
    addMessageRef.current = addMessage;
    saveOnboardingDataRef.current = saveOnboardingData;
  }, [addMessage, saveOnboardingData]);

  // Skip brand setup and go to dashboard
  const handleSkipBrandSetup = useCallback(() => {
    setShowBrandSetup(false);
    navigate('/organizer/dashboard');
  }, [navigate]);

  return (
    <>
      {/* Brand Setup Modal */}
      {showBrandSetup && (
        <div className="brand-setup-modal-overlay">
          <div className="brand-setup-modal">
            <div className="brand-setup-modal-header">
              <h2>Brand Setup</h2>
              <p>Customize how your screening experience looks to candidates</p>
              <button 
                className="skip-brand-btn"
                onClick={handleSkipBrandSetup}
              >
                Skip for now →
              </button>
            </div>
            <div className="brand-setup-modal-content">
              <BrandingSetup organizerId={organizerId} />
            </div>
            <div className="brand-setup-modal-footer">
              <button 
                className="done-brand-btn"
                onClick={() => navigate('/organizer/dashboard')}
              >
                Done - Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Onboarding UI */}
    <div className="voice-onboarding-container">
      <div className="voice-onboarding-card">
        <h1>Welcome to SearchJob 👋</h1>
        <p className="subtitle">I'll ask you a few questions to personalize your experience</p>

        <div className="chat-container">
          <div className="chat-messages">
            {conversation.map((msg) => (
              <div key={msg.id} className={`message ${msg.role}`}>
                <div className="message-bubble">
                  <div className="message-text">{msg.text}</div>
                </div>
              </div>
            ))}
            {recognisingText && (
              <div className="message user">
                <div className="message-bubble recognizing">
                  <div className="message-text">{recognisingText}</div>
                </div>
              </div>
            )}
          </div>

          <div className="chat-status">
            {isSpeaking ? (
              <div className="speaking-indicator">
                <span className="pulse-dot"></span>
                Speaking...
              </div>
            ) : isListening ? (
              <div className="listening-indicator">
                <span className="pulse-dot"></span>
                Listening...
              </div>
            ) : isProcessing ? (
              <div className="processing-indicator">Processing...</div>
            ) : (
              <div className="ready-indicator">Ready</div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default VoiceAssistantOnboarding;
